import React, { useEffect, useRef } from "react"
import { useAppflow } from "app/hooks/useAppFlow"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { SubmitHandler, useForm } from "react-hook-form"
import {
  getFormData,
  getSimulateProposalsResponseStatus,
  getValidatedStep
} from "state/form/selectors"
import {
  acceptProposalsThunk,
  fetchProposalsThunk,
  setData,
  setValidatedStep,
  trackLeadThunk,
} from "state/form/slice"
import { FormStateData } from "state/form/types"
import Header from "app/components/Form/Header"
import Form from "app/components/Form"
import Footer from "app/components/Form/Footer"
import { getTrackLeadPayload } from "../trackLeadPayload"
import { ProductComponent } from "../types"
import { financialLeasingSteps } from "./trackLeadPayload"
import Step1 from "./steps/Step1"
import Step2 from "./steps/Step2"
import Step3 from "./steps/Step3"
import Step4 from "./steps/Step4"
import Step5 from "./steps/Step5"
import Step6 from "./steps/Step6"

enum FormSteps {
  BANK_STEP = 1,
  USER_INFO_STEP = 2,
  COMPANY_INFO_STEP = 3,
  INVESTMENT_STEP = 4,
  DESIRED_FINANCING_STEP = 5,
  VIEW_PROPOSALS_STEP = 6
}

export const FinancialLeasing: React.FC<ProductComponent> = ({
  currentStep
}) => {
  const { navigate, clientType, carType, product, step, path, bank } =
    useAppflow()

  const formData = useSelector(getFormData)
  const validatedStep = useSelector(getValidatedStep)
  const { isPending } = useSelector(getSimulateProposalsResponseStatus)
  const nativeNavigate = useNavigate()
  const dispatch = useDispatch()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const prevStepRef = useRef<number | undefined>()
  const { current: prevStep } = prevStepRef

  const { control, getValues } = useForm<FormStateData>({
    defaultValues: formData
  })

  const STEPS_COMPONENTS: { [key: number]: React.FC<any> } = {
    1: Step1,
    2: Step2,
    3: Step3,
    4: Step4,
    5: Step5,
    6: Step6
  }

  const numberOfSteps = Object.keys(STEPS_COMPONENTS).length

  useEffect(() => {
    if (!step) return
    const activeStep = Math.min(validatedStep + 1, numberOfSteps)

    if (currentStep > validatedStep + 1 || currentStep > numberOfSteps) {
      navigate(clientType!, carType, product, activeStep)
    } else if (currentStep === 0 || isNaN(currentStep)) {
      navigate(clientType!, carType, product, 1)
    }
  }, [
    step,
    validatedStep,
    currentStep,
    navigate,
    clientType,
    carType,
    product,
    numberOfSteps
  ])

  useEffect(() => {
    prevStepRef.current = currentStep
    if (!prevStep) return

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    })
  }, [currentStep])

  const handleFormSubmit: SubmitHandler<FormStateData> = async payload => {
    dispatch(setData({ ...payload, bank: bank }))
    dispatch(setValidatedStep(currentStep))

    switch (currentStep) {
      case FormSteps.BANK_STEP:
        break
      case FormSteps.USER_INFO_STEP:
        dispatch(
          trackLeadThunk(
            getTrackLeadPayload({
              step: currentStep,
              payload,
              config: financialLeasingSteps,
              product: product!,
              carType: carType!,
              path,
              leadRecordType: clientType,
              bank: bank
            })
          )
        )
        break
      case FormSteps.COMPANY_INFO_STEP:
        dispatch(
          trackLeadThunk(
            getTrackLeadPayload({
              step: currentStep,
              payload,
              config: financialLeasingSteps,
              product: product!,
              carType: carType!,
              path,
              leadRecordType: clientType
            })
          )
        )
        break
      case FormSteps.INVESTMENT_STEP:
        dispatch(
          trackLeadThunk(
            getTrackLeadPayload({
              step: currentStep,
              payload,
              config: financialLeasingSteps,
              product: product!,
              carType: carType!,
              path,
              leadRecordType: clientType
            })
          )
        )
        break
      case FormSteps.DESIRED_FINANCING_STEP:
        dispatch(
          trackLeadThunk(
            getTrackLeadPayload({
              step: currentStep,
              payload,
              config: financialLeasingSteps,
              product: product!,
              carType: carType!,
              path,
              leadRecordType: clientType
            })
          )
        )
        dispatch(fetchProposalsThunk(payload))
        break
      case FormSteps.VIEW_PROPOSALS_STEP:
        dispatch(acceptProposalsThunk(payload))
        dispatch(
          trackLeadThunk(
            getTrackLeadPayload({
              step: currentStep,
              payload,
              config: financialLeasingSteps,
              product: product!,
              carType: carType!,
              path,
              leadRecordType: clientType,
              journeyCompleted: true
            })
          )
        )
        break
      default:
        break
    }

    if (currentStep === numberOfSteps) {
      nativeNavigate("/success")
    } else {
      navigate(clientType!, carType, product, currentStep + 1)
    }
  }

  const handlePrevStep = () => {
    navigate(clientType!, carType, product, currentStep - 1)
  }

  const Component: React.FC<any> | undefined = STEPS_COMPONENTS[currentStep]

  return (
    <>
      <Header ref={scrollRef} numberOfSteps={numberOfSteps} />
      <Form
        defaultValues={formData}
        onValid={handleFormSubmit}
        isPending={isPending}
      >
        {Component && <Component control={control} getValues={getValues} />}
        <Footer onPrev={handlePrevStep} numberOfSteps={numberOfSteps} />
      </Form>
    </>
  )
}
